// src/services/tokenService.js
import { STORAGE_KEYS, SESSION } from '../api/constants';
import { parseJwt } from '../utils/jwtUtils';
import AuthChannelService, { AUTH_EVENTS } from './authChannelService';

// In-memory fallback (cleared on tab close/hard refresh)
let inMemoryAccessToken = null;
let inMemoryRefreshToken = null;

// Proactive refresh timer
let refreshTimer = null;
let refreshCallback = null;

class TokenService {
  // Token getters with validation
  static getAccessToken() {
    if (inMemoryAccessToken) return inMemoryAccessToken;
    try {
      return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    } catch (error) {
      return null;
    }
  }

  static getRefreshToken() {
    if (inMemoryRefreshToken) return inMemoryRefreshToken;
    try {
      return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    } catch (error) {
      return null;
    }
  }

  static getUser() {
    try {
      // 🔒 SECURITY: Changed from localStorage to sessionStorage
      // User data should be cleared when tab/browser closes (XSS protection)
      const userStr = sessionStorage.getItem(STORAGE_KEYS.USER);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // Token setters
  static setAccessToken(token) {
    if (!token) return;
    inMemoryAccessToken = token;
    try { sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token); } catch {}
    this.updateLastActivity();
  }

  static setRefreshToken(token, expiryTime) {
    if (!token) return;
    inMemoryRefreshToken = token;
    try { sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token); } catch {}
    if (expiryTime) {
      try { sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY, expiryTime); } catch {}
    }
  }

  /**
   * Get refresh token expiry time
   * @returns {Date|null} Refresh token expiry date
   */
  static getRefreshTokenExpiry() {
    try {
      const expiry = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY);
      return expiry ? new Date(expiry) : null;
    } catch {
      return null;
    }
  }

  /**
   * Check if refresh token is expired
   * @returns {boolean} True if refresh token is expired
   */
  static isRefreshTokenExpired() {
    const expiry = this.getRefreshTokenExpiry();
    if (!expiry) return true;
    return new Date() >= expiry;
  }

  static setUser(user) {
    if (!user) {
      console.warn('Invalid user data provided');
      return;
    }
    try {
      // 🔒 SECURITY: Changed from localStorage to sessionStorage
      // User data (including roles & permissions) should NOT persist after tab close
      // This reduces XSS attack surface significantly
      sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('Error setting user data:', error);
    }
  }

  // Bulk operations
  static setTokens({ accessToken, refreshToken, refreshTokenExpiryTime, user }, options = {}) {
    const { broadcast = true, isLogin = false } = options;
    
    try {
      if (accessToken) this.setAccessToken(accessToken);
      if (refreshToken) this.setRefreshToken(refreshToken, refreshTokenExpiryTime);
      if (user) this.setUser(user);
      this.updateLastActivity();
      
      // Broadcast to other tabs
      if (broadcast) {
        const sessionData = { accessToken, refreshToken, refreshTokenExpiryTime, user };
        if (isLogin) {
          AuthChannelService.broadcastLogin(sessionData);
        } else {
          AuthChannelService.broadcastTokenUpdate(sessionData);
        }
      }
    } catch (error) {
      console.error('Error setting tokens:', error);
      this.clearTokens();
    }
  }

  static clearTokens(options = {}) {
    const { broadcast = true } = options;
    
    try {
      // 🔒 SECURITY: Clear all authentication data from sessionStorage
      try { sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN); } catch {}
      try { sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN); } catch {}
      try { sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_EXPIRY); } catch {}
      try { sessionStorage.removeItem(STORAGE_KEYS.USER); } catch {}
      
      // Clear activity tracking from localStorage
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      
      // Clear in-memory fallbacks
      inMemoryAccessToken = null;
      inMemoryRefreshToken = null;
      
      // Broadcast logout to other tabs
      if (broadcast) {
        AuthChannelService.broadcastLogout();
      }
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  }
  
  /**
   * Apply session data received from another tab
   * @param {Object} sessionData - { accessToken, refreshToken, refreshTokenExpiryTime, user }
   */
  static applySessionFromBroadcast(sessionData) {
    if (!sessionData) return;
    
    const { accessToken, refreshToken, refreshTokenExpiryTime, user } = sessionData;
    
    // Apply without broadcasting back (prevent infinite loop)
    this.setTokens(
      { accessToken, refreshToken, refreshTokenExpiryTime, user },
      { broadcast: false }
    );
    
    console.log('🔄 TokenService: Session applied from another tab');
  }
  
  /**
   * Get current session data for broadcasting
   * @returns {Object|null} Session data or null if not authenticated
   */
  static getSessionData() {
    if (!this.isAuthenticated()) return null;
    
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      refreshTokenExpiryTime: this.getRefreshTokenExpiry()?.toISOString(),
      user: this.getUser(),
    };
  }

  // Session management
  static updateLastActivity() {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  }

  static getLastActivity() {
    const lastActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    return lastActivity ? parseInt(lastActivity, 10) : null;
  }

  // Security validation methods
  static isTokenExpired(token) {
    if (!token) return true;
    
    try {
      const decoded = parseJwt(token);
      if (!decoded || !decoded.exp) return true;
      
      // Convert exp to milliseconds and compare with current time
      const currentTime = Date.now();
      const expiryTime = decoded.exp * 1000; // JWT exp is in seconds
      
      return currentTime >= expiryTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Check if token needs refresh (within threshold)
   * @param {string} token - JWT token
   * @returns {boolean} True if token should be refreshed proactively
   */
  static shouldRefreshToken(token) {
    if (!token) return false;
    
    try {
      const decoded = parseJwt(token);
      if (!decoded || !decoded.exp) return false;
      
      const currentTime = Date.now();
      const expiryTime = decoded.exp * 1000;
      const timeUntilExpiry = expiryTime - currentTime;
      
      // Refresh if within threshold (default 5 minutes before expiry)
      return timeUntilExpiry > 0 && timeUntilExpiry <= SESSION.REFRESH_THRESHOLD;
    } catch {
      return false;
    }
  }

  /**
   * Get time until token expires (in milliseconds)
   * @param {string} token - JWT token
   * @returns {number} Milliseconds until expiry, 0 if expired or invalid
   */
  static getTimeUntilExpiry(token) {
    if (!token) return 0;
    
    try {
      const decoded = parseJwt(token);
      if (!decoded || !decoded.exp) return 0;
      
      const currentTime = Date.now();
      const expiryTime = decoded.exp * 1000;
      const timeUntilExpiry = expiryTime - currentTime;
      
      return Math.max(0, timeUntilExpiry);
    } catch {
      return 0;
    }
  }

  /**
   * Setup proactive token refresh timer
   * @param {Function} onRefreshNeeded - Callback when refresh is needed
   */
  static setupProactiveRefresh(onRefreshNeeded) {
    // Clear existing timer
    this.clearProactiveRefresh();
    
    refreshCallback = onRefreshNeeded;
    
    const token = this.getAccessToken();
    if (!token) return;
    
    const timeUntilExpiry = this.getTimeUntilExpiry(token);
    if (timeUntilExpiry <= 0) {
      // Token already expired, trigger refresh immediately
      if (refreshCallback) refreshCallback();
      return;
    }
    
    // Schedule refresh before expiry (at threshold time)
    const refreshTime = Math.max(0, timeUntilExpiry - SESSION.REFRESH_THRESHOLD);
    
    console.log(`🔄 TokenService: Scheduling proactive refresh in ${Math.round(refreshTime / 1000)}s`);
    
    refreshTimer = setTimeout(() => {
      console.log('🔄 TokenService: Proactive token refresh triggered');
      if (refreshCallback) refreshCallback();
    }, refreshTime);
  }

  /**
   * Clear proactive refresh timer
   */
  static clearProactiveRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    refreshCallback = null;
  }

  // Utility methods
  static isAuthenticated() {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token) && !!this.getUser();
  }

  static hasValidTokens() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!accessToken && !!refreshToken && !this.isTokenExpired(accessToken);
  }
  
  // GÜVENLİK: Aktif verification session kontrolü
  static hasActiveVerificationSession() {
    try {
      const storedEmail = this.getStoredEmail();
      const lastRequestTime = localStorage.getItem('last_code_request_time');
      
      if (!storedEmail || !lastRequestTime) {
        return false;
      }
      
      // 15 dakikalık timeout kontrolü
      const timeSinceLastRequest = Date.now() - parseInt(lastRequestTime);
      const FIFTEEN_MINUTES = 15 * 60 * 1000;
      
      return timeSinceLastRequest <= FIFTEEN_MINUTES;
    } catch (error) {
      console.error('Error checking verification session:', error);
      return false;
    }
  }
  
  // GÜVENLİK: Verification session'ını temizle
  static clearVerificationSession() {
    try {
      this.setStoredEmail(null);
      
      const VERIFICATION_KEYS = [
        'verification_end_time',
        'verification_max_time',
        'verification_original_max_time', 
        'last_code_request_time',
        'auto_resend_count'
      ];
      
      VERIFICATION_KEYS.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing verification session:', error);
    }
  }
  
  static getStoredEmail() {
    return localStorage.getItem(STORAGE_KEYS.EMAIL);
  }
  
  static setStoredEmail(email) {
    if (email) {
      localStorage.setItem(STORAGE_KEYS.EMAIL, email);
    } else {
      localStorage.removeItem(STORAGE_KEYS.EMAIL);
    }
  }
}

export default TokenService;